import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { comboService, itemService, perfilService, type Combo, type Item, type PerfilDTO } from '../services';
import { companyConfigService } from '../services/companyConfigService';
import { NEIGHBORHOODS, type Neighborhood } from '../domain/neighborhoods';
import { useToast } from '../hooks/useToast';
import { pedidoService } from '../services/api';

interface ItemPedidoForm {
  catalogKey: string;
  itemId: string;
  nome: string;
  quantidade: number;
  precoUnitario: number;
  observacao?: string;
  isCombo?: boolean;
  comboId?: string;
  comboNome?: string;
  numberCombo?: number;
}

interface CatalogOption {
  key: string;
  id: string;
  name: string;
  price: number;
  isCombo: boolean;
  displayName: string;
  comboNome?: string;
  numberCombo?: number;
  category?: string;
}

const enderecoDefault = {
  rua: 'Não informado',
  numero: 'S/N',
  bairro: 'Não informado',
  cidade: 'Não informado',
  estado: '--',
  cep: '00000-000',
  complemento: '',
};

const FRETE_FIXO_PADRAO = 5;
const FRETE_BAIRROS_10: readonly Neighborhood[] = [
  'Lagoa de Fora',
  'Sul Mar',
  'Fernão Capelo',
  'Onda Azul',
] as const;
const FRETE_BAIRROS_13: readonly Neighborhood[] = ['Rua Nova', 'Figueirinha'] as const;

const FRETE_BAIRROS_20: readonly Neighborhood[] = [
  'Areias Claras',
  'Sombrio (Centro)'
] as const;

const normalizeNeighborhood = (value: string): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const matchNeighborhood = (value: string): Neighborhood | null => {
  const normalized = normalizeNeighborhood(value);
  if (!normalized) return null;
  const found = NEIGHBORHOODS.find(
    (option) => normalizeNeighborhood(option) === normalized,
  );
  return (found ?? null) as Neighborhood | null;
};

const calcularTaxaEntregaPorBairro = (bairro: string, possuiItens: boolean): number => {
  if (!possuiItens) {
    return 0;
  }

  const matched = matchNeighborhood(bairro);

  if (matched && FRETE_BAIRROS_10.includes(matched)) {
    return 10;
  }

  if (matched && FRETE_BAIRROS_13.includes(matched)) {
    return 13;
  }

  if (matched && FRETE_BAIRROS_20.includes(matched)) {
    return 20;
  }

  if (!matched) {
    const normalized = normalizeNeighborhood(bairro);
    if (FRETE_BAIRROS_10.some((nome) => normalizeNeighborhood(nome) === normalized)) {
      return 10;
    }
    if (FRETE_BAIRROS_13.some((nome) => normalizeNeighborhood(nome) === normalized)) {
      return 13;
    }
  }

  return FRETE_FIXO_PADRAO;
};

const paymentMethods = ['Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 'PIX'];

const BRAZIL_STATES = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const;

const orderChannels = [
  { value: 'WHATSAPP', label: 'WhatsApp / Balcão' },
  { value: 'MAIS_DELIVERY', label: 'Mais Delivery' },
] as const;

const MENU_CATEGORY_ORDER = ['promocao', 'tradicionais', 'especiais', 'combos', 'bebidas'] as const;

const getCategoryPriority = (category?: string): number => {
  const normalized = (category || '').toLowerCase();
  const index = MENU_CATEGORY_ORDER.indexOf(normalized as (typeof MENU_CATEGORY_ORDER)[number]);
  return index === -1 ? MENU_CATEGORY_ORDER.length : index;
};

const formatTelefone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }

  if (digits.length <= 10) {
    // formato fixo: (DD) XXXX-XXXX
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  // formato celular: (DD) XXXXX-XXXX
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const PedidosAvulsosPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const [items, setItems] = useState<Item[]>([]);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [cliente, setCliente] = useState('');
  const [telefone, setTelefone] = useState('');
  const [enderecoTexto, setEnderecoTexto] = useState('');
  const [bairroEntrega, setBairroEntrega] = useState('');
  const [cidadeEntrega, setCidadeEntrega] = useState('');
  const [estadoEntrega, setEstadoEntrega] = useState('');
  const [observacao, setObservacao] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [canalPedido, setCanalPedido] = useState<(typeof orderChannels)[number]['value']>('WHATSAPP');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const [taxaEntrega, setTaxaEntrega] = useState('0');
  const [isentarTaxa, setIsentarTaxa] = useState(false);
  const [itensPedido, setItensPedido] = useState<ItemPedidoForm[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [isLoadingPerfil, setIsLoadingPerfil] = useState(false);
  const [perfilEncontrado, setPerfilEncontrado] = useState(false);
  const companyDefaultsRef = useRef<{ cidade: string; estado: string }>({ cidade: '', estado: '' });

  useEffect(() => {
    let active = true;
    const loadCompanyConfig = async () => {
      try {
        const config = await companyConfigService.getMyConfig();
        const cidade = config?.endereco?.cidade?.trim() ?? '';
        const estado = config?.endereco?.estado?.trim() ?? '';
        if (!active) return;
        companyDefaultsRef.current = { cidade, estado };
        setCidadeEntrega((prev) => (prev.trim() ? prev : cidade));
        setEstadoEntrega((prev) => (prev.trim() ? prev : estado));
      } catch (error) {
        console.error('Erro ao carregar dados da empresa para Pedido Avulso:', error);
      }
    };

    loadCompanyConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const carregarCatalogo = async () => {
      try {
        setLoadingItems(true);
        const [itemsData, combosData] = await Promise.all([
          itemService.listItems(),
          comboService.listCombos(undefined, true),
        ]);
        setItems(itemsData.filter((i) => i.available));
        setCombos(combosData.filter((combo) => combo.ativo));
      } catch (error: any) {
        showErrorRef.current(error?.message || 'Erro ao carregar itens do cardápio');
      } finally {
        setLoadingItems(false);
      }
    };

    void carregarCatalogo();
  }, []);

  const extractComboNumber = useMemo(
    () =>
      (nome: string): number | undefined => {
        const match = nome.match(/(\d+)/);
        if (match) {
          const parsed = Number(match[1]);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
        return undefined;
      },
    [],
  );

  const catalogOptions = useMemo<CatalogOption[]>(() => {
    const comboOptions: CatalogOption[] = combos.map((combo) => {
      const price = typeof combo.precoPromocional === 'number' ? combo.precoPromocional : combo.precoBase;
      const numberCombo = extractComboNumber(combo.nome);
      const category = (combo.categoria || 'combos').toLowerCase();
      return {
        key: `combo:${combo.id}`,
        id: combo.id,
        name: combo.nome,
        price,
        isCombo: true,
        displayName: combo.nome,
        comboNome: combo.nome,
        numberCombo,
        category,
      };
    });

    const itemOptions: CatalogOption[] = items.map((item) => ({
      key: `item:${item.id}`,
      id: item.id,
      name: item.name,
      price: item.price,
      isCombo: false,
      displayName: item.name,
      category: item.category?.toLowerCase(),
    }));

    return [...itemOptions, ...comboOptions].sort((a, b) => {
      const categoryDiff = getCategoryPriority(a.category) - getCategoryPriority(b.category);
      if (categoryDiff !== 0) {
        return categoryDiff;
      }
      return a.displayName.localeCompare(b.displayName, 'pt-BR', { sensitivity: 'base' });
    });
  }, [combos, items, extractComboNumber]);

  const buildItemFromOption = (option: CatalogOption): ItemPedidoForm => ({
    catalogKey: option.key,
    itemId: option.id,
    nome: option.name,
    quantidade: 1,
    precoUnitario: option.price,
    isCombo: option.isCombo,
    comboId: option.isCombo ? option.id : undefined,
    comboNome: option.isCombo ? option.comboNome ?? option.name : undefined,
    numberCombo: option.isCombo ? option.numberCombo : undefined,
  });

  const handleAddItem = () => {
    if (!catalogOptions.length) {
      return;
    }

    const firstOption = catalogOptions[0];
    setItensPedido((prev) => [...prev, buildItemFromOption(firstOption)]);
  };

  const handleChangeItem = (index: number, catalogKey: string) => {
    const selected = catalogOptions.find((option) => option.key === catalogKey);
    if (!selected) return;

    setItensPedido((prev) => {
      const clone = [...prev];
      const atual = clone[index];
      clone[index] = {
        ...atual,
        ...buildItemFromOption(selected),
        quantidade: atual.quantidade,
        observacao: atual.observacao,
      };
      return clone;
    });
  };

  const handleChangeQuantidade = (index: number, quantidade: number) => {
    if (Number.isNaN(quantidade) || quantidade <= 0) {
      quantidade = 1;
    }

    setItensPedido((prev) => {
      const clone = [...prev];
      clone[index] = { ...clone[index], quantidade };
      return clone;
    });
  };

  const handleChangeObservacaoItem = (index: number, observacao: string) => {
    setItensPedido((prev) => {
      const clone = [...prev];
      clone[index] = { ...clone[index], observacao };
      return clone;
    });
  };

  const handleRemoveItem = (index: number) => {
    setItensPedido((prev) => prev.filter((_, i) => i !== index));
  };

  const valorItens = useMemo(() => {
    return itensPedido.reduce((total, item) => total + item.precoUnitario * item.quantidade, 0);
  }, [itensPedido]);

  const taxaEntregaBase = useMemo(() => {
    const parsed = Number((taxaEntrega || '0').replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }
    return parsed;
  }, [taxaEntrega]);

  const taxaEntregaNumber = useMemo(() => {
    if (isentarTaxa) {
      return 0;
    }
    return taxaEntregaBase;
  }, [isentarTaxa, taxaEntregaBase]);

  const valorTotal = useMemo(() => {
    return valorItens + taxaEntregaNumber;
  }, [valorItens, taxaEntregaNumber]);

  const aplicarPerfilAoFormulario = (perfil: PerfilDTO) => {
    setPerfilEncontrado(true);
    setCliente((prev) => (prev.trim() ? prev : perfil.nome));
    const endereco = perfil.endereco ?? {};
    // Preenche o campo de texto de endereço apenas se estiver vazio
    setEnderecoTexto((prev) => {
      if (prev.trim()) return prev;
      const partes: string[] = [];
      if (endereco.rua) {
        const primeiraParte = `${endereco.rua}${endereco.numero ? `, ${endereco.numero}` : ''}`;
        partes.push(primeiraParte);
      }
      const bairroParte = endereco.bairro || '';
      const cidadeParte = endereco.cidade || '';
      const estadoParte = endereco.estado || '';
      const cepParte = endereco.cep || '';

      const restantePartes: string[] = [];
      if (bairroParte) restantePartes.push(bairroParte);
      if (cidadeParte || estadoParte || cepParte) {
        const cidadeEstadoCep: string[] = [];
        if (cidadeParte) cidadeEstadoCep.push(cidadeParte);
        if (estadoParte) cidadeEstadoCep.push(`- ${estadoParte}`);
        if (cepParte) cidadeEstadoCep.push(`, ${cepParte}`);
        restantePartes.push(cidadeEstadoCep.join(' '));
      }

      if (restantePartes.length > 0) {
        partes.push(restantePartes.join(', '));
      }

      return partes.length > 0 ? partes.join(' - ') : prev;
    });

    setBairroEntrega((prev) => {
      if (prev.trim()) return prev;
      const matched = matchNeighborhood(perfil.endereco?.bairro ?? '');
      return matched ?? '';
    });
    setCidadeEntrega((prev) => {
      if (prev.trim()) return prev;
      return perfil.endereco?.cidade?.trim() || companyDefaultsRef.current.cidade;
    });
    setEstadoEntrega((prev) => {
      if (prev.trim()) return prev;
      const uf = perfil.endereco?.estado?.trim() || companyDefaultsRef.current.estado;
      return uf;
    });
    setFormaPagamento((prev) => (prev.trim() ? prev : perfil.formaPagamento ?? prev));
  };

  const telefoneDigits = useMemo(() => telefone.replace(/\D/g, ''), [telefone]);

  const canSubmit = useMemo(() => {
    return (
      !!cliente.trim() &&
      itensPedido.length > 0 &&
      valorItens > 0 &&
      !!formaPagamento.trim() &&
      !!cidadeEntrega.trim() &&
      !!estadoEntrega.trim() &&
      !!bairroEntrega.trim() &&
      !isSubmitting
    );
  }, [cliente, itensPedido.length, valorItens, formaPagamento, bairroEntrega, cidadeEntrega, estadoEntrega, isSubmitting]);

  // Mantém taxa de entrega em sincronia com o bairro informado, espelhando regra do cardápio
  useEffect(() => {
    const taxaCalculada = calcularTaxaEntregaPorBairro(bairroEntrega, itensPedido.length > 0).toString();
    setTaxaEntrega((prev) => (prev === taxaCalculada ? prev : taxaCalculada));
  }, [bairroEntrega, itensPedido.length]);

  const formatPrice = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const resumoPedido = useMemo(() => {
    if (itensPedido.length === 0) {
      return '';
    }

    const telefoneFmt = telefone.replace(/\D/g, '');
    const enderecoLinha = enderecoTexto.trim() || 'Não informado';
    const bairroLinha = bairroEntrega.trim() || 'Não informado';
    const cidadeLinha = cidadeEntrega.trim() || 'Não informado';
    const estadoLinha = estadoEntrega.trim() || 'Não informado';
    const pagamentoLinha = formaPagamento.trim() || 'Não informado';
    const obsGerais = observacao.trim();

    const itemsSummary = itensPedido
      .map((item) => `${item.quantidade}x ${item.nome} - ${formatPrice(item.precoUnitario * item.quantidade)}`)
      .join('\n');

    const subtotal = valorItens;
    const frete = taxaEntregaNumber;
    const desconto = 0;
    const totalFinal = valorTotal;

    return (
      `Cliente: ${cliente || 'Não informado'}\n` +
      `Telefone: ${telefoneFmt || 'Não informado'}\n` +
      `Endereço: ${enderecoLinha}\n` +
      `Bairro: ${bairroLinha}\n` +
      `Cidade/UF: ${cidadeLinha} - ${estadoLinha}\n` +
      `Pagamento: ${pagamentoLinha}` +
      `\nCanal: ${orderChannels.find((option) => option.value === canalPedido)?.label ?? canalPedido}` +
      (obsGerais ? `\nObservações: ${obsGerais}` : '') +
      `\n\nItens do pedido:\n${itemsSummary}\n\n` +
      `Subtotal: ${formatPrice(subtotal)}\n` +
      `Frete: ${formatPrice(frete)}\n` +
      `Cupom: -${formatPrice(desconto)}\n` +
      `Total: ${formatPrice(totalFinal)}`
    );
  }, [
    itensPedido,
    valorItens,
    taxaEntregaNumber,
    valorTotal,
    cliente,
    telefone,
    enderecoTexto,
    bairroEntrega,
    cidadeEntrega,
    estadoEntrega,
    formaPagamento,
    observacao,
    canalPedido,
  ]);

  const parseEnderecoTexto = () => {
    if (!enderecoTexto.trim()) {
      return {
        ...enderecoDefault,
        cidade: cidadeEntrega.trim() || enderecoDefault.cidade,
        estado: estadoEntrega.trim() || enderecoDefault.estado,
      };
    }

    const endereco = { ...enderecoDefault };
    try {
      const [ruaNumeroParte, restante] = enderecoTexto.trim().split(' - ');

      if (ruaNumeroParte) {
        const [rua, numero] = ruaNumeroParte.split(',').map((parte) => parte.trim());
        if (rua) endereco.rua = rua;
        if (numero) endereco.numero = numero;
      }

      if (restante) {
        const [bairroParte, cidadeEstadoCepParte] = restante.split(',').map((parte) => parte.trim());
        if (bairroParte) endereco.bairro = bairroParte;

        if (cidadeEstadoCepParte) {
          const [cidadeParte, estadoCepParte] = cidadeEstadoCepParte.split('-').map((parte) => parte.trim());
          if (cidadeParte) endereco.cidade = cidadeParte;

          if (estadoCepParte) {
            const [estadoParte, cepParte] = estadoCepParte.split(',').map((parte) => parte.trim());
            if (estadoParte) endereco.estado = estadoParte;
            if (cepParte) endereco.cep = cepParte;
          }
        }
      }
    } catch (error) {
      console.warn('Não foi possível interpretar o endereço informado. Utilizando padrão.', error);
    }

    return {
      ...endereco,
      cidade: cidadeEntrega.trim() || endereco.cidade,
      estado: estadoEntrega.trim() || endereco.estado,
    };
  };

  const resetForm = () => {
    setCliente('');
    setTelefone('');
    setEnderecoTexto('');
    setBairroEntrega('');
    setCidadeEntrega(companyDefaultsRef.current.cidade);
    setEstadoEntrega(companyDefaultsRef.current.estado);
    setObservacao('');
    setFormaPagamento('');
    setCanalPedido('WHATSAPP');
    setTaxaEntrega('0');
    setIsentarTaxa(false);
    setItensPedido([]);
    setPerfilEncontrado(false);
    setAttemptedSubmit(false);
  };

  const handleBlurTelefone = async () => {
    const digits = telefoneDigits;
    if (!digits) return;

    setIsLoadingPerfil(true);
    try {
      const perfil = await perfilService.getPerfil(digits);
      if (perfil) {
        aplicarPerfilAoFormulario(perfil);
      } else {
        setPerfilEncontrado(false);
      }
    } catch (error: any) {
      console.error('Erro ao buscar perfil do cliente no Pedido Avulso:', error);
      // Não mostra erro para não atrapalhar fluxo do operador
    } finally {
      setIsLoadingPerfil(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      const endereco = parseEnderecoTexto();
      endereco.bairro = bairroEntrega.trim() || endereco.bairro;

      const payload = {
        cliente: cliente.trim(),
        telefone: telefoneDigits || undefined,
        endereco,
        itens: itensPedido.map((item) => ({
          id: String(item.itemId),
          nome: item.nome,
          quantidade: item.quantidade,
          precoUnitario: item.precoUnitario,
          observacao: item.observacao || undefined,
          isCombo: item.isCombo ?? false,
          comboId: item.isCombo ? item.comboId : undefined,
          comboNome: item.isCombo ? item.comboNome : undefined,
          numberCombo: item.isCombo ? item.numberCombo : undefined,
        })),
        valorItens,
        taxaEntrega: taxaEntregaNumber,
        observacao: observacao.trim() || undefined,
        couponCode: undefined,
        formaPagamento: formaPagamento || undefined,
        canal: canalPedido,
      };

      await pedidoService.criarAvulso(payload);

      // Atualiza ou cria perfil do cliente com base nos dados do pedido
      if (telefoneDigits) {
        const perfilPayload: PerfilDTO = {
          telefone: telefoneDigits,
          nome: cliente.trim() || 'Cliente',
          endereco: {
            rua: endereco.rua,
            numero: endereco.numero,
            bairro: bairroEntrega.trim() || endereco.bairro,
            cidade: cidadeEntrega.trim() || endereco.cidade,
            estado: estadoEntrega.trim() || endereco.estado,
            cep: endereco.cep,
            complemento: endereco.complemento,
          },
          formaPagamento: formaPagamento || undefined,
        };

        try {
          await perfilService.salvarPerfil(perfilPayload);
        } catch (error) {
          console.error('Erro ao salvar perfil do cliente no Pedido Avulso:', error);
        }
      }

      showSuccess('Pedido avulso criado com sucesso.');
      resetForm();
      setConfirmDialogOpen(false);
      setAttemptedSubmit(false);
    } catch (error: any) {
      console.error('Erro ao criar pedido avulso:', error);
      showError(error?.response?.data?.error || error?.message || 'Erro ao criar pedido avulso');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenConfirmDialog = () => {
    if (!canSubmit) {
      setAttemptedSubmit(true);
      showError(
        'Preencha os campos obrigatórios (nome, bairro, cidade, estado, forma de pagamento) e adicione pelo menos um item.',
      );
      return;
    }
    setAttemptedSubmit(false);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    if (isSubmitting) return;
    setConfirmDialogOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Pedido Avulso
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Lance rapidamente pedidos recebidos por WhatsApp ou balcão e envie para a produção.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Dados do cliente
            </Typography>

            <TextField
              label="Nome do cliente"
              fullWidth
              margin="dense"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
            />

            <TextField
              label="Telefone"
              fullWidth
              margin="dense"
              value={telefone}
              onChange={(e) => setTelefone(formatTelefone(e.target.value))}
              onBlur={handleBlurTelefone}
              helperText={
                isLoadingPerfil
                  ? 'Buscando dados do cliente...'
                  : perfilEncontrado
                    ? 'Dados do cliente carregados a partir do telefone informado.'
                    : 'Ao informar o telefone e sair do campo, os dados serão buscados automaticamente (se existirem).'
              }
            />

            <TextField
              label="Endereço (opcional)"
              fullWidth
              margin="dense"
              value={enderecoTexto}
              onChange={(e) => setEnderecoTexto(e.target.value)}
              helperText="Ex: Rua X, 123 - Bairro Y, Cidade - SC, 88955-000"
            />

            <TextField
              select
              required
              label="Bairro (para cálculo do frete)"
              fullWidth
              margin="dense"
              value={bairroEntrega}
              onChange={(e) => setBairroEntrega(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              error={attemptedSubmit && !bairroEntrega.trim()}
              helperText={
                attemptedSubmit && !bairroEntrega.trim()
                  ? 'Selecione um bairro para continuar.'
                  : 'Selecione o bairro para calcular automaticamente a taxa de entrega.'
              }
            >
              {NEIGHBORHOODS.map((neighborhood) => (
                <MenuItem key={neighborhood} value={neighborhood}>
                  {neighborhood}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              required
              label="Cidade"
              fullWidth
              margin="dense"
              value={cidadeEntrega}
              onChange={(e) => setCidadeEntrega(e.target.value)}
              error={attemptedSubmit && !cidadeEntrega.trim()}
              helperText={attemptedSubmit && !cidadeEntrega.trim() ? 'Informe a cidade.' : 'Cidade atendida pela empresa.'}
            />

            <TextField
              select
              required
              label="Estado"
              fullWidth
              margin="dense"
              value={estadoEntrega}
              onChange={(e) => setEstadoEntrega(e.target.value)}
              error={attemptedSubmit && !estadoEntrega.trim()}
              helperText={attemptedSubmit && !estadoEntrega.trim() ? 'Selecione o estado.' : 'Estado (UF) da entrega.'}
            >
              <MenuItem value="" disabled>
                Selecione o estado
              </MenuItem>
              {BRAZIL_STATES.map((uf) => (
                <MenuItem key={uf} value={uf}>
                  {uf}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Observações gerais (opcional)"
              fullWidth
              margin="dense"
              multiline
              minRows={3}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />

            <TextField
              select
              required
              label="Forma de pagamento"
              fullWidth
              margin="dense"
              value={formaPagamento}
              onChange={(e) => setFormaPagamento(e.target.value)}
            >
              {paymentMethods.map((method) => (
                <MenuItem key={method} value={method}>
                  {method}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              required
              label="Canal do pedido"
              fullWidth
              margin="dense"
              value={canalPedido}
              onChange={(e) => setCanalPedido(e.target.value as (typeof orderChannels)[number]['value'])}
              helperText="Informe por qual canal o pedido foi recebido."
            >
              {orderChannels.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Itens do pedido</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={loadingItems || !catalogOptions.length}
              >
                Adicionar item
              </Button>
            </Box>

            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '50%' }}>Item</TableCell>
                    <TableCell align="right" sx={{ width: 96 }}>
                      Qtd.
                    </TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      Vlr. unitário
                    </TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      Total
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Obs.</TableCell>
                    <TableCell align="center" sx={{ width: 52 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itensPedido.map((item, index) => {
                    const totalLinha = item.precoUnitario * item.quantidade;
                    return (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell sx={{ width: '50%' }}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={item.catalogKey}
                              onChange={(e) => handleChangeItem(index, e.target.value)}
                              inputProps={{
                                style: {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                              }}
                            >
                              {catalogOptions.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                  {option.displayName}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell align="right" sx={{ width: 96 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantidade}
                              onChange={(e) => handleChangeQuantidade(index, Number(e.target.value))}
                              inputProps={{ min: 1, style: { textAlign: 'center' } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 90 }}>
                            {item.precoUnitario.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 90 }}>
                            {totalLinha.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }} />
                          <TableCell align="center" sx={{ width: 52 }}>
                            <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Observações do item (opcional)"
                              value={item.observacao ?? ''}
                              onChange={(e) => handleChangeObservacaoItem(index, e.target.value)}
                            />
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}

                  {itensPedido.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Nenhum item adicionado. Clique em "Adicionar item" para começar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Taxa de entrega"
                  fullWidth
                  value={isentarTaxa ? '0' : taxaEntrega}
                  disabled
                  helperText={
                    itensPedido.length === 0
                      ? 'Adicione itens para calcular o frete.'
                      : isentarTaxa
                          ? 'Taxa isenta para este pedido.'
                          : 'Calculado automaticamente a partir do bairro.'
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isentarTaxa}
                      onChange={(event) => setIsentarTaxa(event.target.checked)}
                      color="primary"
                    />
                  }
                  label="Isentar taxa de entrega"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle1">
                  Valor dos itens:{' '}
                  <strong>
                    {valorItens.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </strong>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" align="right">
                  Total:{' '}
                  {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Grid>
            </Grid>

            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={resetForm} disabled={isSubmitting}>
                Limpar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenConfirmDialog}
                disabled={!canSubmit}
              >
                Lançar pedido
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar lançamento do pedido</DialogTitle>
        <DialogContent>
          <Typography>
            Cliente: <strong>{cliente}</strong>
          </Typography>
          <Typography>
            Itens: <strong>{itensPedido.length}</strong>
          </Typography>
          <Typography>
            Canal:{' '}
            <strong>{orderChannels.find((option) => option.value === canalPedido)?.label ?? canalPedido}</strong>
          </Typography>
          <Typography>
            Total:{' '}
            <strong>
              {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </strong>
          </Typography>

          {resumoPedido && (
            <Box
              component="pre"
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {resumoPedido}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting || !canSubmit}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PedidosAvulsosPage;
